import React from 'react'
import {
  TrendingUp,
  ShieldCheck,
  Building2,
  Zap,
  ArrowRight,
  MessageSquare,
  Mail,
  BarChart3,
  CheckCircle2,
  Lock,
} from 'lucide-react'
import type { LandingBadgeMetric } from '../../../types/branding.types'

interface ExecutiveHeroProps {
  brandName?: string | null
  heroTag?: string | null
  headline?: string | null
  subheadline?: string | null
  ctaText?: string | null
  metrics?: LandingBadgeMetric[]
  contactPhone?: string | null
  contactEmail?: string | null
  onCtaClick: () => void
}

const DEFAULT_METRICS: LandingBadgeMetric[] = [
  {
    value: '+R$ 480M',
    label: 'Volume financeiro anual gerenciado',
    icon: 'TrendingUp',
    description: 'Processados e auditados com precisão',
  },
  {
    value: '99.8%',
    label: 'Acurácia em conciliações contábeis',
    icon: 'ShieldCheck',
    description: 'SLA de conformidade e integridade',
  },
  {
    value: '+220',
    label: 'Grupos empresariais atendidos',
    icon: 'Building2',
    description: 'Consultoria e BPO estratégico contínuo',
  },
]

function renderMetricIcon(iconName?: string) {
  const norm = (iconName || '').toLowerCase().replace(/[^a-z0-9]/g, '')
  if (norm.includes('shield') || norm.includes('check') || norm.includes('seguranca')) {
    return <ShieldCheck className="w-5 h-5 text-bronze" strokeWidth={1.75} />
  }
  if (norm.includes('building') || norm.includes('company') || norm.includes('empresa')) {
    return <Building2 className="w-5 h-5 text-bronze" strokeWidth={1.75} />
  }
  if (norm.includes('zap') || norm.includes('bolt') || norm.includes('rapidez')) {
    return <Zap className="w-5 h-5 text-bronze" strokeWidth={1.75} />
  }
  if (norm.includes('chart') || norm.includes('bar') || norm.includes('grafico')) {
    return <BarChart3 className="w-5 h-5 text-bronze" strokeWidth={1.75} />
  }
  if (norm.includes('lock') || norm.includes('cadeado')) {
    return <Lock className="w-5 h-5 text-bronze" strokeWidth={1.75} />
  }
  return <TrendingUp className="w-5 h-5 text-bronze" strokeWidth={1.75} />
}

export const ExecutiveHero: React.FC<ExecutiveHeroProps> = ({
  brandName,
  heroTag,
  headline,
  subheadline,
  ctaText,
  metrics,
  contactPhone,
  contactEmail,
  onCtaClick,
}) => {
  const displayTag = heroTag || 'Consultoria & BPO Financeiro Estratégico'
  const displayHeadline =
    headline || `${brandName || 'Gestão Financeira Executiva'} — Inteligência, Governança e Alta Performance`
  const displaySubheadline =
    subheadline ||
    'Assuma o controle absoluto do seu fluxo de caixa, DRE em tempo real e conciliação bancária diária com o suporte de consultores especialistas.'
  const displayCta = ctaText || 'Agendar Diagnóstico Financeiro'

  const activeMetrics = metrics && metrics.length > 0 ? metrics : DEFAULT_METRICS

  const handleWhatsApp = () => {
    if (!contactPhone) return
    const digits = contactPhone.replace(/\D/g, '')
    const cleanPhone = (digits.length === 10 || digits.length === 11) && !digits.startsWith('55') ? `55${digits}` : digits
    const msg = encodeURIComponent(
      `Olá! Acessei a página da ${brandName || 'empresa'} e gostaria de agendar uma conversa sobre BPO e consultoria financeira.`
    )
    window.open(`https://wa.me/${cleanPhone}?text=${msg}`, '_blank', 'noopener,noreferrer')
  }

  return (
    <section
      id="top"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 px-6 lg:px-10 border-b border-border overflow-hidden bg-background text-foreground"
    >
      {/* Glow e texturas de fundo Seven UI */}
      <div className="absolute inset-0 hero-warm-glow pointer-events-none opacity-70" />

      {/* Selo Editorial da Seção */}
      <div className="absolute top-24 right-6 lg:right-10 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze uppercase">
        01 — INÍCIO
      </div>

      <div className="relative max-w-[1360px] mx-auto grid lg:grid-cols-[1.05fr_1fr] gap-14 lg:gap-16 items-center text-left">
        {/* Coluna Esquerda: Copywriting Editorial e Ações */}
        <div>
          {/* Badge de Autoridade Superior */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-border-strong bg-primary-light/70 text-bronze text-[10px] font-semibold tracking-[0.28em] uppercase">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bronze opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-bronze" />
            </span>
            <span>{displayTag}</span>
          </div>

          {/* Headline Principal */}
          <h1 className="mt-8 font-display font-bold text-[2rem] sm:text-4xl lg:text-[3.75rem] leading-[0.98] tracking-[-0.03em] text-foreground">
            {displayHeadline}
          </h1>

          {/* Subheadline Descritiva */}
          <p className="mt-8 max-w-xl text-lg text-text-3 leading-relaxed">
            {displaySubheadline}
          </p>

          {/* Grupo de Ações / CTAs */}
          <div className="mt-10 flex flex-wrap gap-4">
            <button
              onClick={onCtaClick}
              className="group inline-flex items-center gap-2 bg-bronze hover:bg-bronze-hover text-white px-8 py-4 text-[13px] font-semibold tracking-wide transition-all hover:-translate-y-0.5 shadow-[var(--shadow-bronze)] cursor-pointer"
            >
              <span>{displayCta}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
            </button>

            {contactPhone ? (
              <button
                onClick={handleWhatsApp}
                className="inline-flex items-center gap-2 border border-border-strong text-foreground px-8 py-4 text-[13px] font-semibold tracking-wide hover:bg-surface-2 transition-all cursor-pointer"
              >
                <MessageSquare className="w-4 h-4 text-bronze" />
                <span>Falar no WhatsApp</span>
              </button>
            ) : contactEmail ? (
              <a
                href={`mailto:${contactEmail}`}
                className="inline-flex items-center gap-2 border border-border-strong text-foreground px-8 py-4 text-[13px] font-semibold tracking-wide hover:bg-surface-2 transition-all cursor-pointer"
              >
                <Mail className="w-4 h-4 text-bronze" />
                <span>Enviar E-mail</span>
              </a>
            ) : (
              <a
                href="#governanca"
                className="inline-flex items-center gap-2 border border-border-strong text-foreground px-8 py-4 text-[13px] font-semibold tracking-wide hover:bg-surface-2 transition-all cursor-pointer"
              >
                <span>Conhecer Pilares</span>
              </a>
            )}
          </div>

          {/* Badges de Garantia e Confiança */}
          <div className="mt-14 pt-8 border-t border-border grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-2xl">
            {[
              [ShieldCheck, 'Auditoria e', 'fechamento 24h'],
              [CheckCircle2, 'Governança', 'DRE & EBITDA'],
              [TrendingUp, 'Diagnóstico', 'estratégico'],
            ].map(([Icon, l1, l2], i) => (
              <div key={i} className="flex flex-col gap-2">
                <Icon className="w-5 h-5 text-bronze" strokeWidth={1.6} />
                <span className="text-[12px] text-text-3 leading-tight font-medium">
                  {l1 as string}
                  <br />
                  {l2 as string}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Coluna Direita: Painel Executivo / Grid de Métricas Seven UI */}
        <div className="relative">
          <div className="absolute -inset-10 rounded-full blur-3xl opacity-40 pointer-events-none bg-primary-light" />

          <div className="relative border border-border bg-surface shadow-[var(--shadow-card)] p-6 sm:p-8">
            {/* Cantoneiras Arquiteturais */}
            <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t-2 border-l-2 border-bronze/60" />
            <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t-2 border-r-2 border-bronze/60" />
            <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b-2 border-l-2 border-bronze/60" />
            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b-2 border-r-2 border-bronze/60" />

            {/* Cabeçalho do Painel */}
            <div className="flex items-center justify-between pb-5 mb-6 border-b border-border">
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-text-3">
                  Governança & Auditoria Ativa
                </span>
              </div>
              <span className="text-[11px] font-mono text-bronze font-semibold uppercase tracking-wider">
                BPO {brandName || 'Executivo'}
              </span>
            </div>

            {/* Grid de Indicadores Corporativos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {activeMetrics.map((metric, idx) => (
                <div
                  key={idx}
                  className="relative p-5 border border-border bg-surface-2 hover:border-border-strong transition-all flex flex-col justify-between group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 border border-border bg-surface text-bronze group-hover:border-border-strong transition-colors">
                      {renderMetricIcon(metric.icon)}
                    </div>
                    <span className="text-[10px] font-mono font-semibold uppercase tracking-widest text-text-4">
                      0{idx + 1}
                    </span>
                  </div>
                  <div>
                    <div className="text-2xl sm:text-3xl font-display font-bold font-mono text-bronze tabular-nums tracking-tight mb-1">
                      {metric.value}
                    </div>
                    <div className="text-xs font-semibold text-foreground mb-1 leading-snug">
                      {metric.label}
                    </div>
                    {metric.description && (
                      <div className="text-[11px] text-text-3 leading-tight line-clamp-2">
                        {metric.description}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Linha de Status de Conformidade */}
            <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-[11px] text-text-3">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-bronze" />
                <span>Conformidade contábil e conciliação contínua</span>
              </span>
              <span className="font-mono text-text-4 font-medium">SLA D+0</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
